/**
 * ResetPassword — public page to set a new password from a reset link.
 *
 * URL: /reset-password?token=<raw_reset_token>
 *
 * Differs from AcceptInvite in two ways:
 *   1. The token is 1h TTL (vs invitation's 72h).
 *   2. On success the backend does NOT auto-log-in. The user must
 *      navigate to /login and authenticate fresh. (Safer if the reset
 *      was attacker-triggered.)
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '@/api/client';
import {
  Button,
  Input,
  Card,
  CardContent,
  GradientText,
  ParticleBackground,
  Spinner,
} from '@/components/common';
import { Lock, Key, AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ready'; emailMasked: string }
  | { kind: 'submitting'; emailMasked: string }
  | { kind: 'success' };

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPhase({ kind: 'invalid', message: 'no reset token provided' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await authApi.resetStatus(token);
        if (cancelled) return;
        if (!status.valid) {
          setPhase({ kind: 'invalid', message: 'reset link is no longer valid' });
        } else {
          setPhase({ kind: 'ready', emailMasked: status.email_masked });
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detail = (err as any)?.response?.data?.detail || 'link expired';
        setPhase({ kind: 'invalid', message: typeof detail === 'string' ? detail : 'link expired' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const policyChecks = [
    { label: 'at least 12 characters', ok: password.length >= 12 },
    { label: 'one uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'one lowercase letter', ok: /[a-z]/.test(password) },
    { label: 'one digit', ok: /[0-9]/.test(password) },
    { label: 'one symbol', ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const policyOk = policyChecks.every((c) => c.ok);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (phase.kind !== 'ready') return;
    setFormError(null);

    if (!policyOk) {
      setFormError('password does not meet the policy below');
      return;
    }
    if (!passwordsMatch) {
      setFormError('passwords do not match');
      return;
    }

    setPhase({ kind: 'submitting', emailMasked: phase.emailMasked });
    try {
      await authApi.resetConfirm(token, password);
      setPhase({ kind: 'success' });
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail;
      let msg = 'could not complete; please try again';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        msg = `password rejected: ${detail.errors.join('; ')}`;
      }
      setFormError(msg);
      setPhase({ kind: 'ready', emailMasked: phase.emailMasked });
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <ParticleBackground particleCount={60} />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute top-6 left-6 z-20"
      >
        <Link to="/login" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} />
          <span>Back to Sign In</span>
        </Link>
      </motion.div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-xl bg-primary-50 border border-primary/20">
                <Key className="w-10 h-10 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <GradientText text="Set New Password" />
            </h1>
          </div>

          <Card variant="glass" padding="lg">
            <CardContent>
              {phase.kind === 'loading' && (
                <div className="text-center py-8">
                  <Spinner size="xl" />
                  <p className="mt-4 text-gray-500">Validating reset link…</p>
                </div>
              )}

              {phase.kind === 'invalid' && (
                <div className="text-center py-8">
                  <AlertTriangle className="mx-auto w-12 h-12 text-amber-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Reset link is no longer valid</p>
                  <p className="text-gray-500 text-sm mb-6">{phase.message}</p>
                  <Link to="/forgot-password">
                    <Button variant="primary" fullWidth>
                      Request a new reset link
                    </Button>
                  </Link>
                </div>
              )}

              {(phase.kind === 'ready' || phase.kind === 'submitting') && (
                <>
                  <p className="text-sm text-gray-600 mb-6">
                    Setting a new password for <strong>{phase.emailMasked}</strong>.
                    All your active sessions will be signed out.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {formError && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600">
                        <AlertTriangle size={20} />
                        <span className="text-sm">{formError}</span>
                      </div>
                    )}

                    <Input
                      label="New Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Choose a strong password"
                      leftIcon={<Lock size={18} />}
                      fullWidth
                      required
                      autoComplete="new-password"
                      disabled={phase.kind === 'submitting'}
                    />

                    <Input
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter the same password"
                      leftIcon={<Lock size={18} />}
                      fullWidth
                      required
                      autoComplete="new-password"
                      disabled={phase.kind === 'submitting'}
                    />

                    <ul className="space-y-1 text-xs">
                      {policyChecks.map((c) => (
                        <li key={c.label} className={c.ok ? 'text-emerald-600' : 'text-gray-500'}>
                          {c.ok ? '✓' : '○'} {c.label}
                        </li>
                      ))}
                      <li className={passwordsMatch ? 'text-emerald-600' : 'text-gray-500'}>
                        {passwordsMatch ? '✓' : '○'} passwords match
                      </li>
                    </ul>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      isLoading={phase.kind === 'submitting'}
                      disabled={!policyOk || !passwordsMatch || phase.kind === 'submitting'}
                    >
                      Set New Password
                    </Button>
                  </form>
                </>
              )}

              {phase.kind === 'success' && (
                <div className="text-center py-8">
                  <CheckCircle2 className="mx-auto w-12 h-12 text-emerald-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Password updated</p>
                  <p className="text-gray-500 text-sm">Redirecting to sign in…</p>
                </div>
              )}
            </CardContent>
          </Card>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-xs text-gray-600 mt-8"
          >
            &copy; 2014-2026 MadHat Media, Inc. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
